import mitt, { type Emitter } from 'mitt';

import type { Preference } from '~/models';

type WSApiEvent =
  | {
      type: 'order-status-update';
      message: string;
    }
  | {
      type: 'preferences-update';
      message: Preference;
    };

type WSInternalEvents = {
  type: 'ws-clonnection-closed';
  message: undefined;
};

type WSEvent = WSApiEvent | WSInternalEvents;

type MittEvents = {
  [K in WSEvent['type']]: Extract<WSEvent, { type: K }>['message'];
};

let bus: Emitter<MittEvents>;

const createWebSocket = async <T extends Emitter<MittEvents>>(
  eventBus: T
): Promise<void> => {
  const endpoint = useBaseURL();

  const origin = window.location.origin.replace(/^http/, 'ws');

  const webSocket = endpoint.startsWith('/')
    ? new WebSocket(`${origin}${endpoint}/ws/`)
    : new WebSocket(`${endpoint}/ws/`);

  webSocket.onmessage = (event) => {
    const data: WSApiEvent = JSON.parse(event.data);
    eventBus.emit(data.type, data.message);
  };

  webSocket.onopen = (evt) => {
    if (!import.meta.dev) {
      return;
    }

    console.info('Socket connection openned', evt);
  };

  webSocket.onerror = (err) => {
    if (import.meta.dev) {
      console.error('Socket encountered error: ', err, 'Closing socket');
    }

    webSocket.close();
  };

  webSocket.onclose = (e) => {
    if (import.meta.dev) {
      console.info('Socket connection was closed', e.reason);
    }

    eventBus.emit('ws-clonnection-closed');
  };
};

const createBus = async (): Promise<Emitter<MittEvents>> => {
  const eventBus = mitt<MittEvents>();

  await createWebSocket(eventBus);

  eventBus.on('ws-clonnection-closed', () => {
    setTimeout(async () => {
      await createWebSocket(eventBus);
    }, 10000);
  });

  return eventBus;
};

export const useWebSocket = async () => {
  if (!bus) {
    bus = await createBus();
  }

  return {
    webSocket: {
      on: bus.on,
      off: bus.off
    }
  };
};
